import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
    Box, Flex, Text, Link
} from 'components/ui';
import { colors, radii } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import resourceWrapper from 'utils/framework/resourceWrapper';
import basketConstants from 'constants/Basket';
import Actions from 'Actions';
import languageLocale from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import { HEADER_VALUE } from 'constants/authentication';
import Location from 'utils/Location';
import helpersUtils from 'utils/Helpers';
import bccUtils from 'utils/BCC';
import SDDRougeTestV2InfoModal from 'utils/SDDRougeTestV2InfoModal';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import FulfillmentCard from 'components/ProductPage/FulfillmentMethods/FulfillmentCard';
import FreeReturnsBanner from 'components/ProductPage/FulfillmentMethods/FreeReturnsBanner';
import ShippingDetailsPanel from 'components/ProductPage/FulfillmentMethods/ShippingDetailsPanel';
import FastDeliveryDetailsPanel from 'components/ProductPage/FulfillmentMethods/FastDeliveryDetailsPanel';
import PickupDetailsPanel from 'components/ProductPage/FulfillmentMethods/PickupDetailsPanel';
import AutoReplenishRow from 'components/ProductPage/FulfillmentMethods/AutoReplenishRow';
import SDULandingPageModal from 'components/GlobalModals/SDULandingPageModal';
import useFulfillmentMethodsProps from 'components/ProductPage/FulfillmentMethods/hooks/useFulfillmentMethodsProps';
import { formatCardEta, formatPanelEta } from 'components/ProductPage/FulfillmentMethods/utils/formatEta';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { DELIVERY_OPTIONS } = basketConstants;
const { deferTaskExecution } = helpersUtils;
const { SAME_DAY_UNLIMITED_MODAL_US, SAME_DAY_UNLIMITED_MODAL_CA } = bccUtils.MEDIA_IDS;
const getText = resourceWrapper(languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR));

const styles = {
    root: {
        backgroundColor: colors.nearWhite
    },
    divider: {
        height: 1,
        width: '100%',
        backgroundColor: colors.lightGray
    }
};

/**
 * FreeShippingSignInTitle
 *
 * Signed-out title for the Free Shipping card (SHP-5258): "Sign in" as a real,
 * independently-focusable link followed by "for" and the bold card title.
 * Rendered only when `FulfillmentCard`'s `interactive` variant is active.
 *
 * @param {object} props
 * @param {Function} props.onSignInClick - Opens the sign-in modal; also stops
 *     propagation so the click doesn't toggle the card's own radio selection.
 * @returns {JSX.Element}
 */
const FreeShippingSignInTitle = ({ onSignInClick }) => (
    <Text
        is='p'
        fontSize='base'
        lineHeight='tight'
    >
        {getText(
            'freeShippingSignInCardText',
            false,
            <Link
                onClick={onSignInClick}
                color='blue'
                underline={true}
                fontSize='base'
                padding={2}
                margin={-2}
                children={getText('signInLinkLabel')}
            />
        )}{' '}
        <Text
            is='span'
            fontWeight='bold'
            color='black'
            children={getText('freeShippingCardTitle')}
        />
    </Text>
);

FreeShippingSignInTitle.propTypes = {
    onSignInClick: PropTypes.func.isRequired
};

/**
 * Builds the 3-card radiogroup config (Free Shipping / Fast Delivery / Pickup).
 * Extracted from the component body so the gift-card/online-only/signed-out
 * branching doesn't push `FulfillmentMethods` over the complexity budget.
 *
 * @param {object} params
 * @returns {Array<object>} Card configs consumed by `FulfillmentCard`.
 */
const buildCards = ({
    isSignedIn,
    onSignInClick,
    shippingCardEta,
    sameDayTitleText,
    isGiftCard,
    fastDeliveryCardEta,
    isOnlineOnly,
    pickupCardEta,
    pickupCardSecondaryText,
    pickupMessageColor
}) => [
    {
        id: 'fulfillmentMethod-standard',
        method: DELIVERY_OPTIONS.STANDARD,
        iconName: 'shippingTruck',
        title: isSignedIn
            ? getText('freeShippingCardTitle')
            : getText('freeShippingSignInAriaLabel', false, getText('signInLinkLabel'), getText('freeShippingCardTitle')),
        titleContent: isSignedIn ? undefined : <FreeShippingSignInTitle onSignInClick={onSignInClick} />,
        interactive: !isSignedIn,
        etaText: shippingCardEta,
        etaColor: 'green'
    },
    {
        id: 'fulfillmentMethod-fastDelivery',
        method: DELIVERY_OPTIONS.SAME_DAY,
        iconName: 'bag',
        title: sameDayTitleText,
        disabled: isGiftCard,
        secondaryText: isGiftCard ? getText('fastDeliveryNotAvailable') : undefined,
        etaText: fastDeliveryCardEta,
        etaColor: 'green',
        enableMarkdownSubtitle: true
    },
    {
        id: 'fulfillmentMethod-pickup',
        method: DELIVERY_OPTIONS.PICKUP,
        iconName: 'store',
        title: isGiftCard ? getText('availableInAllStoresCardTitle') : isOnlineOnly ? getText('onlineOnly') : getText('pickupCardTitle'),
        disabled: isGiftCard || isOnlineOnly,
        secondaryText: pickupCardSecondaryText,
        etaText: isGiftCard || isOnlineOnly ? undefined : pickupCardEta,
        etaColor: pickupMessageColor
    }
];

/**
 * FulfillmentMethods
 *
 * PDP Fulfillment Methods redesign (SHP-5111): a 3-card radiogroup (Free
 * Shipping / Fast Delivery / Pickup), the shipping details panel, and the
 * Auto-Replenish row. SHP-5258 adds a signed-out "Sign in" prompt to the Free
 * Shipping card and gates its selection styling behind `userUtils.isSignedIn()`.
 * SHP-5262 relabels the Pickup card to "Online Only" and disables it for
 * online-only SKUs, mirroring the SHP-5234 gift-card treatment.
 * SHP-5238 adds the Pickup selected state (details panel + store card text).
 *
 * @param {object} props
 * @param {object} [props.currentProduct] - The current product from Redux.
 * @param {object} [props.fulfillment] - Fulfillment selection handlers from `useFulfillment`.
 * @param {object} [props.commerce] - Commerce state from `useCommerce`, forwarded to `AutoReplenishRow`.
 * @param {boolean} [props.displayOrderCutoffCountdown] - Forwarded to `useFulfillmentMethodsProps` for the Fast Delivery cutoff countdown.
 * @returns {JSX.Element}
 */
const FulfillmentMethods = ({ currentProduct, fulfillment, commerce, displayOrderCutoffCountdown }) => {
    // SHP-5111 delivers the visual starting point: Free Shipping is preselected;
    // Fast Delivery / Pickup selected states arrive in follow-up stories.
    const [selectedMethod, setSelectedMethod] = useState(DELIVERY_OPTIONS.STANDARD);
    const [renderSDULandingPage, setRenderSDULandingPage] = useState(false);

    const dispatch = useDispatch();
    const isSignedIn = userUtils.isSignedIn();

    const {
        preferredZipCode,
        shipToHomeMessage,
        showAutoReplenishment,
        isReplenishmentEligible,
        isGiftCard,
        isOnlineOnly,
        sddRadioButtonDisabled,
        availabilityText,
        locationText,
        forText,
        sameDayAvailabilityStatus,
        availabilityTextColor,
        sameDayDeliveryPanelMessage,
        sddRougeTestV2FreeShippingMessage,
        sddRougeTestThreshold,
        nddEnabled,
        isUserSduTrialEligible,
        isUserSDUTrialAllowed,
        isSDUAddedToBasket,
        skuTrialEligibility,
        skuTrialPeriod,
        sameDayTitleText,
        sameDaySubTitle,
        storeName,
        availabilityLabel,
        availabilityColor,
        hasStoreAvailability,
        pickupMessage,
        pickupMessageColor,
        showInStorePickup,
        showCurbsideIndicator,
        showConciergeCurbsideIndicator,
        isInStockAtNearbyStore,
        showNearbyStoreAvailability,
        isPickupOutOfStock
    } = useFulfillmentMethodsProps(currentProduct, displayOrderCutoffCountdown, fulfillment);

    const currentSku = currentProduct?.currentSku;

    // SHP-5258: signed-out clients see a "Sign in" prompt on the Free Shipping card
    // since eligibility is tied to Beauty Insider membership. `stopPropagation` keeps
    // the nested link from also toggling the card's own radio selection.
    const showSignInModal = useCallback(
        () => dispatch(Actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } })),
        [dispatch]
    );

    const handleSignInLinkClick = useCallback(
        e => {
            e.stopPropagation();
            showSignInModal();
        },
        [showSignInModal]
    );

    const fireShippingOptionClickAnalytics = useCallback(
        method => {
            if (typeof digitalData !== 'object' || !digitalData?.page?.attributes) {
                return;
            }

            digitalData.page.attributes.outOfStock = '';

            if (method?.toLowerCase() !== anaConsts.DELIVERY_OPTIONS.Standard) {
                return;
            }

            if (currentProduct?.currentSku?.isOutOfStock) {
                digitalData.page.attributes.outOfStock = `Yes - ${anaConsts.FULFILLMENT_TYPE.Standard}`;
            }

            pdpAnalytics.trackLinkClick({
                actionInfo: anaConsts.EVENT_NAMES.FREE_SHIPPING_RADIO_BUTTON_CLICK,
                prop44: digitalData.page.attributes.outOfStock,
                productStrings: `;${currentProduct?.currentSku?.skuId};;;;eVar26=${currentProduct?.currentSku?.skuId}`
            });
        },
        [currentProduct]
    );

    const handleSelect = useCallback(
        (method, { trackAnalytics = true } = {}) => {
            setSelectedMethod(method);

            deferTaskExecution(() => {
                commerce?.autoReplenishClick?.(false);

                switch (method) {
                    case DELIVERY_OPTIONS.STANDARD:
                        fulfillment?.selectShipped?.();

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(method);
                        }

                        break;
                    case DELIVERY_OPTIONS.SAME_DAY:
                        fulfillment?.selectSameDay?.(fulfillment?.sameDayAvailable);

                        break;
                    case DELIVERY_OPTIONS.PICKUP:
                        fulfillment?.selectPickup?.();

                        break;
                    default:
                        break;
                }
            });
        },
        [commerce, fulfillment, fireShippingOptionClickAnalytics]
    );

    // Guards against a stale selection when the mounted SKU becomes a gift card
    // or online-only item (e.g. a shade/variant change) while Fast Delivery/Pickup
    // was selected — those cards become disabled, so the selection must fall back
    // to shipping. Routed through `handleSelect` (not just `setSelectedMethod`) so
    // the shared `fulfillment` state (e.g. `basketType`) stays in sync with the UI;
    // `trackAnalytics: false` since this is a forced fallback, not a user click.
    useEffect(() => {
        if ((isGiftCard || isOnlineOnly) && selectedMethod !== DELIVERY_OPTIONS.STANDARD) {
            handleSelect(DELIVERY_OPTIONS.STANDARD, { trackAnalytics: false });
        }
    }, [isGiftCard, isOnlineOnly, selectedMethod, handleSelect]);

    const toggleSDULandingPage = useCallback(() => setRenderSDULandingPage(prev => !prev), []);

    const handleShowSDDRougeTestV2InfoModal = useCallback(() => {
        SDDRougeTestV2InfoModal.showModal(toggleSDULandingPage, sddRougeTestThreshold);
    }, [toggleSDULandingPage, sddRougeTestThreshold]);

    const handleFastDeliveryLocationClick = useCallback(() => {
        const entry = Location.isProductPage() ? 'ppage sdd' : '';
        dispatch(
            Actions.showShippingDeliveryLocationModal({
                isOpen: true,
                entry,
                callback: ({ sameDayAvailable: sda }) => {
                    fulfillment?.selectSameDay?.(sda, true);
                },
                sduZipcodeModal: false,
                sku: currentProduct?.currentSku
            })
        );
    }, [dispatch, fulfillment, currentProduct]);

    const shippingCardEta = formatCardEta(shipToHomeMessage);
    const panelEtaText = formatPanelEta(shipToHomeMessage);

    const fastDeliveryCardEta = isGiftCard ? undefined : sameDaySubTitle;

    // Per Figma, when the item is in stock at the selected store the Pickup card
    // secondary text shows the store availability ("In Stock at {store}") instead
    // of the default static ETA.
    const isInStockAtSelectedStore = hasStoreAvailability && availabilityLabel === 'inStock' && Boolean(storeName);
    const pickupCardEta = isInStockAtSelectedStore ? `${getText('inStock')} ${getText('pickupPanelAt')} ${storeName}` : getText('pickupCardEta');

    // The card shows "Out of Stock at {store}" whenever the item is OOS at the selected store,
    // in every scenario — no-nearby (SHP-5232) and nearby (SHP-5280) alike.
    const outOfStockText = isPickupOutOfStock ? (storeName ? getText('outOfStockAt', false, storeName) : getText('outOfStock')) : undefined;
    const pickupCardSecondaryText =
        isGiftCard || isOnlineOnly
            ? getText('pickupNotAvailable')
            : isInStockAtSelectedStore
                ? `${getText('inStock')} ${getText('pickupPanelAt')} ${storeName}`
                : outOfStockText;

    const cards = buildCards({
        isSignedIn,
        onSignInClick: handleSignInLinkClick,
        shippingCardEta,
        sameDayTitleText,
        isGiftCard,
        fastDeliveryCardEta,
        isOnlineOnly,
        pickupCardEta,
        pickupCardSecondaryText,
        pickupMessageColor
    });

    const showFastDeliveryPanel = selectedMethod === DELIVERY_OPTIONS.SAME_DAY;

    const sduMediaId = languageLocale.isCanada() ? SAME_DAY_UNLIMITED_MODAL_CA : SAME_DAY_UNLIMITED_MODAL_US;

    if (typeof digitalData === 'object' && digitalData && digitalData?.page?.attributes) {
        digitalData.page.attributes.isSameDayDeliveryAvailable = !sddRadioButtonDisabled;
    }

    return (
        <Box
            is='fieldset'
            marginTop={4}
            marginBottom={4}
            marginX={['-container', 0]}
            padding={4}
            borderRadius={radii[1]}
            lineHeight='tight'
            css={styles.root}
        >
            <Flex
                flexDirection='column'
                gap={3}
                width='100%'
            >
                <FreeReturnsBanner />
                <Flex
                    flexDirection='column'
                    gap={2}
                    width='100%'
                >
                    <Flex
                        role='radiogroup'
                        aria-label={getText('fulfillmentMethodsGroupAriaLabel')}
                        gap={2}
                        width='100%'
                        alignItems='stretch'
                    >
                        {cards.map(card => {
                            const secondary = card.secondaryText || card.etaText;
                            const ariaLabel = secondary ? `${card.title}, ${secondary}` : card.title;

                            return (
                                <Box
                                    key={card.id}
                                    display='flex'
                                    flex='1 1 0'
                                    minWidth={0}
                                >
                                    <FulfillmentCard
                                        id={card.id}
                                        iconName={card.iconName}
                                        title={card.title}
                                        titleContent={card.titleContent}
                                        etaText={card.etaText}
                                        etaColor={card.etaColor}
                                        secondaryText={card.secondaryText}
                                        disabled={card.disabled}
                                        isSelected={selectedMethod === card.method}
                                        onSelect={() => handleSelect(card.method)}
                                        ariaLabel={ariaLabel}
                                        interactive={card.interactive}
                                        enableMarkdownSubtitle={card.enableMarkdownSubtitle}
                                    />
                                </Box>
                            );
                        })}
                    </Flex>
                    <Box
                        padding={0}
                        borderRadius={radii[1]}
                        backgroundColor='white'
                    >
                        <Box padding={3}>
                            {showFastDeliveryPanel ? (
                                <FastDeliveryDetailsPanel
                                    availabilityText={availabilityText}
                                    locationText={locationText}
                                    forText={forText}
                                    sameDayAvailabilityStatus={sameDayAvailabilityStatus}
                                    availabilityTextColor={availabilityTextColor}
                                    cutoffMessage={sameDayDeliveryPanelMessage}
                                    sddRougeTestV2FreeShippingMessage={sddRougeTestV2FreeShippingMessage}
                                    nddEnabled={nddEnabled}
                                    skuTrialEligibility={skuTrialEligibility}
                                    skuTrialPeriod={skuTrialPeriod}
                                    onOpenSDULandingPage={toggleSDULandingPage}
                                    onShowSDDRougeTestV2InfoModal={handleShowSDDRougeTestV2InfoModal}
                                    onLocationClick={handleFastDeliveryLocationClick}
                                />
                            ) : selectedMethod === DELIVERY_OPTIONS.PICKUP ? (
                                <PickupDetailsPanel
                                    currentProduct={currentProduct}
                                    storeName={storeName}
                                    availabilityLabel={availabilityLabel}
                                    availabilityColor={availabilityColor}
                                    pickupMessage={pickupMessage}
                                    pickupMessageColor={pickupMessageColor}
                                    displayOrderCutoffCountdown={displayOrderCutoffCountdown}
                                    showInStorePickup={showInStorePickup}
                                    showCurbsideIndicator={showCurbsideIndicator}
                                    showConciergeCurbsideIndicator={showConciergeCurbsideIndicator}
                                    isPickupOutOfStock={isPickupOutOfStock}
                                    showNearbyStoreAvailability={showNearbyStoreAvailability}
                                    isInStockAtNearbyStore={isInStockAtNearbyStore}
                                />
                            ) : (
                                <ShippingDetailsPanel
                                    etaText={panelEtaText}
                                    zipCode={preferredZipCode}
                                    currentSku={currentSku}
                                />
                            )}
                            {showAutoReplenishment && selectedMethod === DELIVERY_OPTIONS.STANDARD && (
                                <>
                                    <Box
                                        marginTop={2}
                                        marginBottom={2}
                                        css={styles.divider}
                                    />
                                    <AutoReplenishRow
                                        currentSku={currentSku}
                                        commerce={commerce}
                                        isReplenishmentEligible={isReplenishmentEligible}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </Flex>
            </Flex>
            {renderSDULandingPage && (
                <SDULandingPageModal
                    isOpen={renderSDULandingPage}
                    onDismiss={toggleSDULandingPage}
                    mediaId={sduMediaId}
                    skuTrialPeriod={skuTrialPeriod}
                    isSDUAddedToBasket={isSDUAddedToBasket}
                    isUserSDUTrialEligible={isUserSduTrialEligible}
                    isCanada={languageLocale.isCanada()}
                    skipConfirmationModal={false}
                    isUserSDUTrialAllowed={isUserSDUTrialAllowed}
                />
            )}
        </Box>
    );
};

FulfillmentMethods.propTypes = {
    currentProduct: PropTypes.object,
    fulfillment: PropTypes.object,
    commerce: PropTypes.object,
    displayOrderCutoffCountdown: PropTypes.bool
};

export default wrapFunctionalComponent(FulfillmentMethods, 'FulfillmentMethods');
